import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Card, CardContent, CardMedia, IconButton, Rating, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { Image as ImageIcon, Close as CloseIcon, FavoriteBorder as FavoriteBorderIcon, Favorite as FavoriteIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import CheckDataProduct from '../js/CheckDataProduct';
import { useHandleFavoriteMutation } from '../features/api/rest/apiFavoritesSlice';
import { startSpan } from './OpenTelemetry/OpenTelemetryProvider';
import { setAlertMessage } from '../features/layoutSlice';

function ProductCard({
  ean = '',
  image = '',
  name = 'products.messages.nameNotPresent',
  rating = {
    average: null,
    count: 0,
  },
  favorite = false,
  shouldDisplayRatings = false,
}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const [isFavorite, setIsFavorite] = React.useState(favorite);
  const { ref, inView } = useInView({
    threshold: 0,
  });

  const [handleFavorite] = useHandleFavoriteMutation();

  React.useEffect(() => {
    setIsFavorite(favorite);
  }, [favorite]);

  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    if (inView === true) {
      setIsVisible(true);
      const openSpan = startSpan('Open ProductCard');
      openSpan.setAttribute('EAN', ean);
      openSpan.setAttribute('Name', name);
      openSpan.setAttribute('Rating', rating);
      openSpan.end();
    }
    if (inView === false && isVisible === true) {
      setIsVisible(false);
      const closeSpan = startSpan('Close ProductCard');
      closeSpan.setAttribute('EAN', ean);
      closeSpan.setAttribute('Name', name);
      closeSpan.setAttribute('Rating', rating);
      return () => {
        closeSpan.end();
      };
    }
  }, [inView]);

  const goToProduct = () => {
    if (!isTooltipOpen) navigate(`/products/${ean}${searchParams.has('search') ? `?search=${searchParams.get('search')}` : ''}`);
  };

  const cardMediaImage = (
    <div style={{ height: '60%', marginBottom: '20px', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '5px' }}>
      <img src={image} alt={t('products.research.imageAlt')} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    </div>
  );
  const cardMediaPlaceHolder = (
    <CardMedia sx={{ height: '60%', mb: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#EBEBEF', color: 'text.secondary', borderRadius: '5px' }}>
      <ImageIcon sx={{ color: 'text.tertiary', fontSize: '2.5rem' }} />
    </CardMedia>
  );
  const TooltipClose = (
    <IconButton onClick={() => setIsTooltipOpen(false)} sx={{ position: 'absolute', top: 0, right: 0, color: 'text.secondary' }} size="small">
      <CloseIcon />
    </IconButton>
  );

  const TooltipContent = (
    <>
      {!isDesktop && TooltipClose}
      {rating.average && rating.average > 0 && (
        <>
          <Typography>{t('reviews.tooltip.title')}</Typography>
          <Rating name="read-only" value={rating.average} precision={0.5} readOnly sx={{ mt: 1 }} />
          <Typography variant="h3" sx={{ mb: 1 }}>
            {rating.average.toFixed(1)}
          </Typography>
        </>
      )}

      <Typography variant="body2">{t('reviews.tooltip.text', { count: parseInt(rating.count, 10) })}</Typography>

      {!rating.average && (
        <Button variant="outlined" sx={{ mt: 2 }} color="warning" size="small">
          <NavLink to={`${ean}/editOrCreateReview/`} style={{ textDecoration: 'none', color: 'inherit' }}>
            {t('reviews.tooltip.cta')}
          </NavLink>
        </Button>
      )}
    </>
  );

  // eslint-disable-next-line react/jsx-props-no-spreading
  const RatingTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.text[rating.average ? 'primary' : 'secondary'],
      border: '1px solid #dadde9',
      fontSize: '0.75rem',
      fontWeight: 'bold',
      padding: '1rem',
      position: 'relative',
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: '#dadde9',
    },
  }));

  const setFavorite = async (e) => {
    e.preventDefault();
    try {
      await handleFavorite({ ean, favorite: !isFavorite }).unwrap();
      setIsFavorite(!isFavorite);
    } catch (err) {
      dispatch(setAlertMessage({ text: t('common.messages.notAutorized.feature'), type: 'error' }));
    }
  };

  return (
    <Card onClick={goToProduct} className="Products__Card" ref={ref}>
      {(CheckDataProduct.checkData(image) && cardMediaImage) || cardMediaPlaceHolder}
      {shouldDisplayRatings && (
        <span // eslint-disable-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <RatingTooltip title={TooltipContent} placement="top-start" arrow onOpen={() => setIsTooltipOpen(true)} onClose={() => setIsTooltipOpen(false)} open={isTooltipOpen}>
            <Box sx={{ display: 'inline', position: 'relative' }}>
              <Rating name="read-only" value={rating.average} precision={0.5} readOnly size="small" />
              <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary', position: 'relative', top: -10 }} component="span">
                {rating.count > 0 && rating.count}
              </Typography>
            </Box>
          </RatingTooltip>
          <IconButton onClick={setFavorite} sx={{ ml: 1.5, mb: 1 }} size="small">
            {isFavorite ? <FavoriteIcon htmlColor="#EE2B0C" /> : <FavoriteBorderIcon />}
          </IconButton>
        </span>
      )}
      <CardContent sx={{ p: 0, mt: 1 }}>
        <Typography sx={{ textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: '3', WebkitBoxOrient: 'vertical', textAlign: 'initial', overflow: 'hidden' }} variant="body2" color="text.secondary">
          {t(name)}
        </Typography>
      </CardContent>
    </Card>
  );
}

ProductCard.propTypes = {
  ean: PropTypes.string,
  image: PropTypes.string,
  name: PropTypes.string,
  rating: PropTypes.shape({
    average: PropTypes.number,
    count: PropTypes.number,
  }),
  shouldDisplayRatings: PropTypes.bool,
  favorite: PropTypes.bool,
};

export default ProductCard;
